import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiGrid,
  FiList,
  FiFilter,
  FiSearch,
  FiHeart,
  FiShoppingCart,
  FiEye,
} from "react-icons/fi";
import Footer from "../components/Footer";
import ThreeModelViewer from "../components/ThreeModelViewer";
import artisan1 from "../assets/artisan-1.jpg";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function ProductsType({ type }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("name");
  const [searchTerm, setSearchTerm] = useState("");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [selectedColors, setSelectedColors] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

const buildImageUrl = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // URL complète détectée, on la retourne brute
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Sinon on construit URL locale
  let cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename;

  if (cleanFilename.startsWith("public/")) {
    cleanFilename = cleanFilename.slice("public/".length);
  }

  if (!cleanFilename.startsWith("uploads/")) {
    cleanFilename = `uploads/${cleanFilename}`;
  }

  return `${apiUrl}/${cleanFilename}`.replace(/([^:]\/)\/+/g, "$1");
};

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${apiUrl}/api/shop/products/type/${type}`,
          {
            withCredentials: true,
          }
        );
        setProducts(res.data.products);
        setFilteredProducts(res.data.products);
      } catch (err) {
        setError("Erreur lors du chargement des produits.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [type]);

  useEffect(() => {
    let filtered = [...products];

    if (searchTerm) {
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered = filtered.filter((product) => {
      const price = product.price?.originalPrice || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    if (selectedColors.length > 0) {
      filtered = filtered.filter((product) =>
        product.variantes?.some((variant) =>
          selectedColors.includes(variant.couleur)
        )
      );
    }

    filtered.sort((a, b) => {
      const priceA = a.price?.originalPrice || 0;
      const priceB = b.price?.originalPrice || 0;

      switch (sortBy) {
        case "price-asc":
          return priceA - priceB;
        case "price-desc":
          return priceB - priceA;
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    setFilteredProducts(filtered);
  }, [products, searchTerm, priceRange, selectedColors, sortBy]);

  const handleProductClick = (id) => {
    navigate(`/product/${id}`);
  };

  const getTypeInfo = () => {
    switch (type) {
      case "femme":
        return {
          title: "Babouches pour Femme",
          description:
            "Les babouches marocaines pour femme incarnent l'élégance traditionnelle alliée à une touche contemporaine.",
        };
      case "homme":
        return {
          title: "Babouches pour Homme",
          description:
            "Les babouches marocaines pour homme signées SAFA sont de véritables pièces d'art.",
        };
      case "enfant":
        return {
          title: "Babouches pour Enfant",
          description:
            "Les babouches marocaines pour enfant allient confort et robustesse.",
        };
      default:
        return {
          title: "Collections Spéciales",
          description:
            "Découvrez nos collections spéciales, alliant innovation et artisanat traditionnel.",
        };
    }
  };

  const typeInfo = getTypeInfo();
  const allColors = [
    ...new Set(
      products.flatMap((p) => p.variantes?.map((v) => v.couleur) || [])
    ),
  ];

  const formatPrice = (price) => {
    if (price === undefined || price === null) return "0.00";
    return parseFloat(price).toFixed(2);
  };

  return (
    <>
      <div className="products-container">
        <motion.div
          className="products-hero"
          style={{
            background: `linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.3) 100%), url(${artisan1})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-overlay" />
          <motion.div
            className="hero-content"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <h1>{typeInfo.title}</h1>
            <p>{typeInfo.description}</p>
          </motion.div>
        </motion.div>

        <div className="controls-section">
          <div className="controls-grid">
            <div className="search-container">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                <FiGrid />
              </button>
              <button
                className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                <FiList />
              </button>
            </div>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="name">Nom A-Z</option>
              <option value="price-asc">Prix croissant</option>
              <option value="price-desc">Prix décroissant</option>
            </select>

            <button
              className={`filter-btn ${showFilters ? "active" : ""}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FiFilter />
              Filtres
            </button>

            <div className="product-types-nav">
              <Link to="/products/femme" className="type-btn">
                FEMME
              </Link>
              <Link to="/products/homme" className="type-btn">
                HOMME
              </Link>
              <Link to="/products/enfant" className="type-btn">
                ENFANT
              </Link>
            </div>
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                className="filters-panel"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="filters-grid">
                  <div className="filter-group">
                    <h3>Prix</h3>
                    <div className="price-range">
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min}
                        onChange={(e) =>
                          setPriceRange((prev) => ({
                            ...prev,
                            min: Number(e.target.value),
                          }))
                        }
                        className="price-input"
                      />
                      <span>-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max}
                        onChange={(e) =>
                          setPriceRange((prev) => ({
                            ...prev,
                            max: Number(e.target.value),
                          }))
                        }
                        className="price-input"
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <h3>Couleurs</h3>
                    <div className="color-filters">
                      {allColors.map((color) => (
                        <label key={color} className="color-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedColors.includes(color)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedColors((prev) => [...prev, color]);
                              } else {
                                setSelectedColors((prev) =>
                                  prev.filter((c) => c !== color)
                                );
                              }
                            }}
                          />
                          <span className="color-label">{color}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="products-section">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Chargement des produits...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>{error}</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <motion.div
              className="empty-container"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                ease: "easeOut",
                delay: 0.2,
              }}
            >
              <motion.div
                className="coming-soon-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: 0.4,
                }}
              >
                <motion.div
                  className="ornament-line"
                  initial={{ width: 0 }}
                  animate={{ width: "60px" }}
                  transition={{
                    duration: 0.8,
                    ease: "easeOut",
                    delay: 0.6,
                  }}
                />
                <motion.h2
                  className="coming-soon-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    ease: "easeOut",
                    delay: 0.8,
                  }}
                >
                  Prochaine collection
                </motion.h2>
                <motion.p
                  className="coming-soon-subtitle"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    ease: "easeOut",
                    delay: 1.0,
                  }}
                >
                  bientôt disponible
                </motion.p>
                <motion.div
                  className="ornament-line"
                  initial={{ width: 0 }}
                  animate={{ width: "60px" }}
                  transition={{
                    duration: 0.8,
                    ease: "easeOut",
                    delay: 1.2,
                  }}
                />
                <motion.div
                  className="decorative-elements"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 1.0,
                    ease: "easeOut",
                    delay: 1.4,
                  }}
                >
                  <div className="decorative-dot" />
                  <div className="decorative-dot" />
                  <div className="decorative-dot" />
                </motion.div>
              </motion.div>
            </motion.div>
          ) : (
            <div className={`products-grid ${viewMode}`}>
              <AnimatePresence>
                {filteredProducts.map((product, index) => {
                  const hasPromo =
                    product.price?.promoPrice &&
                    product.price.promoPrice < product.price.originalPrice;
                  const originalPrice = product.price?.originalPrice || 0;
                  const promoPrice = product.price?.promoPrice || originalPrice;
                  const promoPercentage = product.promo || 0;

                  return (
                    <motion.div
                      key={product._id}
                      className={`product-card ${viewMode}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ y: -5, scale: 1.02 }}
                      onClick={() => handleProductClick(product._id)}
                    >
                      <div className="product-image">
                        {product.variantes?.[0]?.images?.[0] ? (
                          (() => {
                            const firstImage = product.variantes[0].images[0];
                            const imageUrl = buildImageUrl(firstImage);

                            if (
                              firstImage.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                            ) {
                              return (
                                <motion.img
                                  src={imageUrl}
                                  alt={product.name}
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ duration: 0.3 }}
                                />
                              );
                            }

                            if (firstImage.match(/\.(glb|gltf|obj)$/i)) {
                              return (
                                <ThreeModelViewer
                                  url={imageUrl}
                                  style={{
                                    width: "100%",
                                    height:
                                      viewMode === "grid" ? "200px" : "150px",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                  }}
                                />
                              );
                            }

                            return <p>Format non supporté</p>;
                          })()
                        ) : (
                          <div className="no-image">Aucune image</div>
                        )}

                        <div className="product-actions">
                          <button className="action-btn">
                            <FiHeart />
                          </button>
                          <button className="action-btn">
                            <FiShoppingCart />
                          </button>
                          <button className="action-btn">
                            <FiEye />
                          </button>
                        </div>
                      </div>

                      <div className="product-info">
                        <h3 className="product-name">{product.name}</h3>
                        <div className="product-price">
                          {hasPromo && (
                            <span className="old-price">
                              {formatPrice(originalPrice)} MAD
                            </span>
                          )}
                          <span
                            className={`current-price ${
                              hasPromo ? "promo" : ""
                            }`}
                          >
                            {formatPrice(hasPromo ? promoPrice : originalPrice)}{" "}
                            MAD
                            {hasPromo && (
                              <span className="promo-badge">
                                -{promoPercentage}%
                              </span>
                            )}
                          </span>
                        </div>

                        {product.variantes?.[0]?.couleur && (
                          <div className="product-color">
                            Couleur : {product.variantes[0].couleur}
                          </div>
                        )}

                        {viewMode === "list" &&
                          product.variantes?.[0]?.images?.length > 1 && (
                            <div className="product-variants">
                              {product.variantes[0].images
                                .slice(1, 4)
                                .map((filename, idx) => {
                                  const url = buildImageUrl(filename);
                                  if (
                                    filename.match(
                                      /\.(jpg|jpeg|png|gif|webp)$/i
                                    )
                                  ) {
                                    return (
                                      <img
                                        key={idx}
                                        src={url}
                                        alt={`Variante ${idx + 2}`}
                                        className="variant-image"
                                      />
                                    );
                                  }
                                  return null;
                                })}
                            </div>
                          )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
}

// Styles CSS
const styles = `
.products-container {
  font-family: 'Cormorant Garamond', serif;
  color: #2c1810;
  background: linear-gradient(135deg, #f8f5f0 0%, #f0ebe0 100%);
  min-height: 100vh;
}

.products-hero {
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 700px;
  padding: 0 20px;
}

.hero-content h1 {
  font-size: 2.8rem;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 1px;
  text-shadow: 0 4px 20px rgba(0,0,0,0.8);
  font-family: 'Cormorant Garamond', serif;
}

.hero-content p {
  font-size: 18px;
  font-weight: 300;
  text-shadow: 0 2px 10px rgba(0,0,0,0.6);
  opacity: 0.95;
  line-height: 1.6;
  font-style: italic;
  font-family: 'Cormorant Garamond', serif;
}

.controls-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(150px);
  padding: 20px;
  border-bottom: 1px solid rgba(212, 175, 55, 0.2);
  position: sticky;
  z-index: 10;
}

.controls-grid {
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 15px;
  align-items: center;
  margin: 0 auto;
}

.search-container {
  position: relative;
  max-width: 350px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #d4af37;
  font-size: 1rem;
}

.search-input {
  width: 100%;
  padding: 10px 10px 10px 35px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 20px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  font-family: 'Cormorant Garamond', serif;
  color: #2c1810;
}

.search-input:focus {
  outline: none;
  border-color: #d4af37;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
}

.view-toggle {
  display: flex;
  gap: 3px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 3px;
  border: 1px solid rgba(212, 175, 55, 0.2);
}

.view-btn {
  padding: 6px 10px;
  border: none;
  background: transparent;
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #2c1810;
  font-size: 14px;
  font-family: 'Cormorant Garamond', serif;
}

.view-btn.active {
  background: rgba(212, 175, 55, 0.3);
  color: #2c1810;
  box-shadow: 0 2px 8px rgba(212, 175, 55, 0.3);
}

.view-btn:hover:not(.active) {
  background: rgba(212, 175, 55, 0.1);
  color: #2c1810;
}

.sort-select {
  padding: 8px 12px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Cormorant Garamond', serif;
  color: #2c1810;
}

.sort-select:focus {
  outline: none;
  border-color: #d4af37;
  box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  background: rgba(255, 255, 255, 0.15);
}

.filter-btn {
  padding: 8px 15px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: #2c1810;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-family: 'Cormorant Garamond', serif;
}

.filter-btn.active {
  background: rgba(212, 175, 55, 0.3);
  color: #2c1810;
  border-color: #d4af37;
}

.filter-btn:hover {
  background: rgba(212, 175, 55, 0.1);
  border-color: #d4af37;
  color: #2c1810;
}

.filters-panel {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-top: 1px solid rgba(212, 175, 55, 0.2);
  padding: 20px;
  overflow: hidden;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin: 0 auto;
}

.filter-group h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.price-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 15px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  font-family: 'Cormorant Garamond', serif;
  color: #2c1810;
}

.price-input:focus {
  outline: none;
  border-color: #d4af37;
  background: rgba(255, 255, 255, 0.15);
}

.color-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
}

.color-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.color-checkbox:hover {
  background: rgba(212, 175, 55, 0.1);
}




.color-checkbox input {
  margin: 0;
}

.color-label {
  font-size: 14px;
  color: #5a4a3a;
  font-family: 'Cormorant Garamond', serif;
}

.products-section {
  padding: 20px 10%;
  margin: 0 auto;
}

.loading-container,
.error-container,
.empty-container {
  text-align: center;
  padding: 40px 20px;
  color: #2c1810;
  font-family: 'Cormorant Garamond', serif;
}

.coming-soon-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 30px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  margin: 0 auto;
}

.coming-soon-title {
  font-size: 2.2rem;
  font-weight: 600;
  color: #2c1810;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
  letter-spacing: 1px;
  text-align: center;
}

.coming-soon-subtitle {
  font-size: 1.4rem;
  color: #d4af37;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
  font-style: italic;
  text-align: center;
  font-weight: 400;
}

.ornament-line {
  height: 2px;
  background: linear-gradient(90deg, transparent, #d4af37, transparent);
  border-radius: 1px;
  margin: 10px 0;
}

.decorative-elements {
  display: flex;
  gap: 15px;
  margin-top: 20px;
}

.decorative-dot {
  width: 8px;
  height: 8px;
  background: #d4af37;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}

.decorative-dot:nth-child(2) {
  animation-delay: 0.3s;
}

.decorative-dot:nth-child(3) {
  animation-delay: 0.6s;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.3);
    opacity: 1;
  }
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 2px solid rgba(212, 175, 55, 0.2);
  border-top: 2px solid #d4af37;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.products-grid {
  display: grid;
  gap: 15px;
}

.products-grid.grid {
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
}

.products-grid.list {
  grid-template-columns: 1fr;
}

.product-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(212, 175, 55, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.product-card:hover {
  box-shadow: 0 8px 25px rgba(212, 175, 55, 0.2);
  transform: translateY(-3px);
}

.product-card.grid {
  display: flex;
  flex-direction: column;
}

.product-card.list {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 15px;
  padding: 15px;
}

.product-image {
  position: relative;
  overflow: hidden;
}

.product-card.grid .product-image {
  height: 200px;
}

.product-card.list .product-image {
  height: 150px;
  border-radius: 8px;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}

.product-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: 0;
  transition: all 0.3s ease;
}

.product-card:hover .product-actions {
  opacity: 1;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: #2c1810;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(212, 175, 55, 0.3);
  font-size: 14px;
}

.action-btn:hover {
  background: rgba(212, 175, 55, 0.3);
  color: #2c1810;
  transform: scale(1.1);
}

.product-info {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c1810;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
  line-height: 1.2;
}

.product-price {
  display: flex;
  align-items: center;
  gap: 8px;
}

.old-price {
  text-decoration: line-through;
  color: #999;
  font-size: 0.8rem;
}

.current-price {
  color: #d4af37;
  font-weight: 600;
  font-size: 16px;
  font-family: 'Cormorant Garamond', serif;
  display: flex;
  align-items: center;
  gap: 5px;
}

.current-price.promo {
  color: #ef4444;
}

.promo-badge {
  background: #ef4444;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
}

.product-description {
  color: #5a4a3a;
  line-height: 1.4;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
  font-size: 14px;
}

.product-color {
  font-size: 14px;
  color: #d4af37;
  font-style: italic;
  font-family: 'Cormorant Garamond', serif;
}

.product-variants {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}

.variant-image {
  width: 35px;
  height: 35px;
  object-fit: cover;
  border-radius: 15px;
  border: 1px solid rgba(212, 175, 55, 0.3);
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(212, 175, 55, 0.1);
  color: #2c1810;
  font-size: 14px;
  font-family: 'Cormorant Garamond', serif;
}

@media (max-width: 1024px) {
  .controls-grid {
    grid-template-columns: 1fr auto auto;
    gap: 12px;
  }
  
  .products-grid.grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  
  .product-card.list {
    grid-template-columns: 180px 1fr;
    gap: 12px;
    padding: 12px;
  }
}

@media (max-width: 768px) {
  .hero-content h1 {
    font-size: 2.2rem;
  }
  
  .controls-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .view-toggle {
    justify-content: center;
  }
  
  .products-grid.grid {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
  
  .product-card.list {
    grid-template-columns: 1fr;
    text-align: center;
  }
  
  .product-card.list .product-image {
    height: 200px;
  }
  
  .filters-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .color-filters {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  }
}

.product-types-nav {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
}

.type-btn {
  padding: 8px 15px;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: #2c1810;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 14px;
  font-family: 'Cormorant Garamond', serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-btn:hover {
  background: rgba(212, 175, 55, 0.1);
  border-color: #d4af37;
  color: #2c1810;
}

@media (max-width: 480px) {
  .hero-content h1 {
    font-size: 1.8rem;
  }
  
  .hero-content p {
    font-size: 0.9rem;
  }
  
  .controls-section {
    padding: 15px 10px;
  }
  
  .products-section {
    padding: 15px 20px;
  }
  
  .product-info {
    padding: 10px;
  }
  
  .product-name {
    font-size: 0.9rem;
  }
  
  .products-grid.grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  .product-image canvas {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  border-radius: inherit;
}

}
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
